import { Box, Link, Stack, Typography } from "@mui/material"
import { PageBreadcrumbContainer } from "lib/page-breadcrumb-container"
import { getProjectGroup } from "./controller/get-project-group"
import { refreshOnlyFor } from "lib/graphql/api"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { useParams } from "react-router"
import { Bound } from "lib/@components/binding/Bound"
import { If } from "lib/@components/switch"
import { OverwriteGroup } from "./overwrite/overwrite-group"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { useProject } from "authoring/lib/hooks/use-project"
import { navigateProject, navigateProjects } from "../utilities/project-navigation"
import { AddGroup } from "./add/add-group"
import { IntroductionGroup } from "./introduction/introduction-group"
import { LeadingSummaryGroup } from "./leading-summary/leading-summary-group"
import { GroupStatusBadge } from "../components/group-status-badge"
import { TreeToolbar } from "slot-definitions"
import { ApplyGroupButton } from "../plugins/apply-group-button"
import { UnlockGroupButton } from "../plugins/unlock-group-button"
import { useRef } from "react"
import { GroupType } from "../../../lib/enum/group-type.js"
import { Secure } from "lib/authorization/secure"
import { Frag } from "lib/@components/slot/frag"
import { AUTHORING_EDIT_SCHEDULES_DEMAND } from "authoring/lib/constants/authoring-permissions.mjs"

export function Group() {
    const { projectId, groupId } = useParams()
    const { project } = useProject({ id: projectId })
    const projectGroupResult = getProjectGroup.useResults(
        { projectId, groupId },
        refreshOnlyFor(projectId),
        withInitialValue(IS_LOADING)
    )
    const groupLoading = projectGroupResult === IS_LOADING
    const projectGroup = groupLoading ? undefined : projectGroupResult
    const isOverwriteType = projectGroup?.type === GroupType.OVERWRITE
    const isAddType = projectGroup?.type === GroupType.ADD
    const isIntroductionType = projectGroup?.type === GroupType.INTRODUCTION
    const isLeadingSummaryType = projectGroup?.type === GroupType.LEADING_SUMMARY
    const hasAddedSchedules =
        projectGroup?.addTypeSchedules?.length > 0 ||
        projectGroup?.introTypeSchedules?.length > 0 ||
        projectGroup?.leadingSummaryTypeSchedules?.length > 0
    const hasNewTasks = projectGroup?.newTasks?.length > 0
    const savingRef = useRef(false)

    TreeToolbar.usePlug(
        <Secure demand={AUTHORING_EDIT_SCHEDULES_DEMAND} fallback={<Frag />} priority={998}>
            <UnlockGroupButton />
        </Secure>
    )
    TreeToolbar.usePlug(
        <Secure demand={AUTHORING_EDIT_SCHEDULES_DEMAND} fallback={<Frag />} priority={999}>
            <ApplyGroupButton variant="contained" />
        </Secure>
    )
    const parentCrumbs = [
        <Link key="navigateProject" sx={{ cursor: "pointer" }} onClick={navigateProjects}>
            Work Packages
        </Link>,
        <Link key="navigateCorrelator" sx={{ cursor: "pointer" }} onClick={() => navigateProject(projectId)}>
            {project?.name}
        </Link>,
    ]

    if (groupLoading && !savingRef.current) return <LoadingScreen />
    return (
        <Bound
            projectGroup={projectGroup}
            group={projectGroup}
            isOverwrite={isOverwriteType}
            isAdd={isAddType}
            isIntroduction={isIntroductionType}
            isLeadingSummary={isLeadingSummaryType}
            groupLoading={groupLoading}
            project={project}
            hasAddedSchedules={hasAddedSchedules}
            hasNewTasks={hasNewTasks}
            hideViewMode={true}
            savingRef={savingRef}
        >
            <PageBreadcrumbContainer
                heading={
                    <Stack direction="row" gap={2}>
                        <Typography>{projectGroup?.name}</Typography>
                        <GroupStatusBadge status={projectGroup?.status} />
                    </Stack>
                }
                crumb={projectGroup?.name}
                parentCrumbs={parentCrumbs}
            >
                <Box flex={1}>
                    <If truthy={!!isOverwriteType}>
                        <OverwriteGroup />
                    </If>
                    <If truthy={!!isAddType}>
                        <AddGroup />
                    </If>
                    <If truthy={!!isIntroductionType}>
                        <IntroductionGroup />
                    </If>
                    <If truthy={!!isLeadingSummaryType}>
                        <LeadingSummaryGroup />
                    </If>
                </Box>
            </PageBreadcrumbContainer>
        </Bound>
    )
}
